import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { companyService } from '../../../services/companyService';
import { siteService } from '../../../services/siteService';
import { useLanguage } from '../../../contexts/LanguageContext';

const TeamManagementPanel = ({ siteId }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWorkers();
  }, [siteId]);

  const loadWorkers = async () => {
    try {
      setLoading(true);

      // Strategy:
      // 1. If siteId is provided, load workers for that specific site
      // 2. Otherwise load workers for all of the site_manager's assigned sites
      // 3. Fallback: company-scoped workers

      if (siteId) {
        // Load workers for the specific active site
        const siteWorkersResult = await siteService?.getWorkersBySite(siteId);
        if (!siteWorkersResult?.error && siteWorkersResult?.data?.length > 0) {
          setWorkers(siteWorkersResult?.data);
          return;
        }
      }

      // Get all sites assigned to this site_manager
      const sitesResult = await siteService?.getSitesByUser();
      const assignedSiteIds = (sitesResult?.data || [])?.map(s => s?.id);

      if (assignedSiteIds?.length > 0) {
        const workersResult = await siteService?.getWorkersBySites(assignedSiteIds);
        if (!workersResult?.error && workersResult?.data?.length > 0) {
          setWorkers(workersResult?.data);
          return;
        }
      }

      // Final fallback: company-scoped user_profiles query
      const companyResult = await companyService?.getUserCompany();
      if (!companyResult?.data?.id) {
        setWorkers([]);
        return;
      }

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, role, is_active, company_id')
        ?.eq('company_id', companyResult?.data?.id)
        ?.in('role', ['worker', 'site_manager'])
        ?.eq('is_active', true)
        // jyyb8AfJ — exclude admin-deactivated users from the SM team panel.
        ?.is('deactivated_at', null)
        ?.order('full_name');

      if (!error && data) {
        setWorkers(data);
      }
    } catch (err) {
      console.error('[TeamManagementPanel] loadWorkers error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = filter === 'all' ? workers
    : filter === 'worker' ? workers?.filter(w => w?.role === 'worker')
    : workers?.filter(w => w?.role === 'site_manager');

  const getRoleLabel = (role) => role === 'site_manager' ? t('teamRoleSiteManager') : t('teamRoleWorker');
  const getRoleBadgeColor = (role) => role === 'site_manager' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700';

  const filterTabs = [
    { id: 'all', labelKey: 'teamFilterAll' },
    { id: 'worker', labelKey: 'teamFilterWorkers' },
    { id: 'site_manager', labelKey: 'teamFilterManagers' },
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 overflow-hidden">
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground font-heading">{t('teamTitle')}</h3>
            <p className="text-sm text-muted-foreground">{workers?.length} {workers?.length !== 1 ? t('usersTeams') : t('teamRoleWorker')}</p>
          </div>
          <button
            onClick={() => navigate('/workers')}
            className="text-sm text-accent hover:text-accent/80 font-medium flex items-center gap-1"
          >
            {t('teamManage')} <Icon name="ArrowRight" size={14} />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mt-3">
          {filterTabs?.map(f => (
            <button
              key={f?.id}
              onClick={() => setFilter(f?.id)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === f?.id ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {t(f?.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent" />
        </div>
      ) : filtered?.length === 0 ? (
        <div className="p-6 text-center">
          <Icon name="Users" size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('teamNoMembers')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('teamNoMembersDesc')}</p>
        </div>
      ) : (
        <div className="divide-y divide-border max-h-80 overflow-y-auto">
          {filtered?.map(worker => (
            <button
              key={worker?.id}
              onClick={() => navigate(`/workers/${worker?.id}`)}
              className="w-full px-6 py-4 flex items-center gap-4 hover:bg-muted/50 transition-colors text-left"
            >
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-accent">
                  {worker?.full_name?.split(' ')?.map(n => n?.[0])?.join('') || '?'}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground text-sm truncate">{worker?.full_name || 'Unknown'}</p>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium mt-0.5 ${getRoleBadgeColor(worker?.role)}`}>
                  {getRoleLabel(worker?.role)}
                </span>
              </div>

              <Icon name="ChevronRight" size={16} className="text-muted-foreground flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TeamManagementPanel;
